import React, { useReducer, useContext } from "react";
import axios from "axios";
import DisputeContext from "./DisputeContext";
import DisputeReducer from "./DisputeReducer";
import {
  GET_DISPUTE_BY_ID,
  GET_DISPUTES_SORTED_BY_DATE,
  SET_LOADING,
  DISPUTE_ERROR,
  RETURNED_DATA,
  RETURNED_DATA_ERROR,
} from "../../types";
import AuthContext from "../../auth/AuthContext";

const DisputeState = (props) => {
  const initialState = {
    dispute: null,
    disputes: null,
    order: null,
    error: null,
    returnedData: null,
    returnedDataError: null,
    loading: false,
  };

  const [state, dispatch] = useReducer(DisputeReducer, initialState);
  const { user } = useContext(AuthContext);

  // Set Loading
  const setLoading = () =>
    dispatch({
      type: SET_LOADING,
    });

  const getDisputeById = async (id) => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/paypal/v1/get-dispute/${id}`);
      dispatch({
        type: GET_DISPUTE_BY_ID,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: DISPUTE_ERROR,
        payload: err.response,
      });
    }
  };

  //get latest data from PaypalDisputes
  const getDisputes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/paypal/v1/get-disputes`);

      dispatch({
        type: GET_DISPUTES_SORTED_BY_DATE,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: DISPUTE_ERROR,
        payload: err.response,
      });
    }
  };

  // Send Dispute Message
  const sendDisputeMessage = async (message, dispute_id) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/send-dispute-message", {
        message: message,
        dispute_id: dispute_id,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  // Acknowledge Returned Item
  const acknowledgeReturnedItem = async (dispute_id, note) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/acknowledge-return-item", {
        dispute_id: dispute_id,
        note: note,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  // Provide Supporting Information for dispute
  const provideSupportingInfo = async (dispute_id, note) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/provide-supporting-info", {
        dispute_id: dispute_id,
        note: note,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  // Provide Evidence
  const provideEvidence = async (
    dispute_id,
    evidence_type,
    carrier_name,
    tracking_number,
    note
  ) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/provide-evidence", {
        dispute_id: dispute_id,
        evidence_type: evidence_type,
        carrier_name: carrier_name,
        tracking_number: tracking_number,
        note: note,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  // Appeal dispute
  const appealDispute = async (
    dispute_id,
    evidence_type,
    carrier_name,
    tracking_number,
    note
  ) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/appeal", {
        dispute_id: dispute_id,
        evidence_type: evidence_type,
        carrier_name: carrier_name,
        tracking_number: tracking_number,
        note: note,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  // Escalate dispute to claim
  const escalateDispute = async (dispute_id, note) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/escalate", {
        dispute_id: dispute_id,
        note: note,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  // Accept Claim
  const acceptClaim = async (
    dispute_id,
    note,
    reason,
    amount,
    dispute_reason,
    dispute_status
  ) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/accept-claim", {
        dispute_id: dispute_id,
        note: note,
        reason: reason,
        amount: amount,
        dispute_reason: dispute_reason,
        dispute_status: dispute_status,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  // Make Offer to Resolve Dispute
  const makeOffer = async (dispute_id, note, refund_type, amount) => {
    try {
      setLoading(true);
      const res = await axios.post("/api/paypal/v1/make-offer", {
        dispute_id: dispute_id,
        note: note,
        refund_type: refund_type,
        amount: amount,
        username: user.name,
      });
      dispatch({
        type: RETURNED_DATA,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: RETURNED_DATA_ERROR,
        payload: err.response,
      });
    }
  };

  return (
    <DisputeContext.Provider
      value={{
        dispute: state.dispute,
        disputes: state.disputes,
        order: state.order,
        error: state.error,
        loading: state.loading,
        returnedData: state.returnedData,
        returnedDataError: state.returnedDataError,
        getDisputeById,
        getDisputes,
        setLoading,
        sendDisputeMessage,
        makeOffer,
        escalateDispute,
        provideSupportingInfo,
        provideEvidence,
        acceptClaim,
        appealDispute,
        acknowledgeReturnedItem,
      }}
    >
      {props.children}{" "}
    </DisputeContext.Provider>
  );
};

export default DisputeState;
